import { InputAdornment } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  BenefitDTOBenefitEnum,
  CreateBenefitActivationDTO,
  CustomBenefitOptionsDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TextFieldControl } from '../../../../component/form';
import { BenefitActivationDialogExtensionInterface } from '../../../../extension/interface/benefit-activation-dialog.extension.interface';
import { ActivationDialogExtensions } from '../../ActivationDialog';
import { NonCashLimitWarning } from '../../ActivationDialog/NonCashLimitWarning';

interface CustomBenefitOptions {
  monthlyGrant?: number;
  supplier?: string;
  comment?: string;
  relevantForNonCash?: boolean;
  customBenefitId?: string;
}

const Header: React.FC = () => {
  const { watch } = useFormContext();
  const { employeeId } = useParams();
  const benefitOptions: CustomBenefitOptionsDTO = watch('benefitOptions');

  return (
    <>
      {benefitOptions.relevantForNonCash && (
        <NonCashLimitWarning
          employeeId={employeeId}
          benefit={BenefitDTOBenefitEnum.Custom}
          customBenefitId={benefitOptions.customBenefitId}
          monthlyBudget={benefitOptions.monthlyGrant! * 100}
        />
      )}
    </>
  );
};

const Fields: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { control } = useFormContext();

  return (
    <>
      <Grid container columnSpacing={2} rowSpacing={1} marginBottom={1}>
        <Grid size={12}>
          <TextFieldControl
            control={control}
            variant="outlined"
            name="benefitOptions.supplier"
            label={t('CUSTOM.fields.supplier')}
            margin="dense"
            fullWidth
            sx={{ mt: 0 }}
          />
        </Grid>
        <Grid size={12}>
          <TextFieldControl
            control={control}
            multiline
            variant="outlined"
            name="benefitOptions.comment"
            label={t('CUSTOM.fields.comment')}
            margin="dense"
            fullWidth
          />
        </Grid>
      </Grid>
    </>
  );
};

const InlineField: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { control } = useFormContext();

  return (
    <TextFieldControl
      slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
      rules={{
        min: 0,
        max: 5000,
      }}
      type="number"
      control={control}
      variant="outlined"
      name="benefitOptions.monthlyGrant"
      label={t('CUSTOM.fields.monthlyGrant')}
      margin="dense"
      fullWidth
    />
  );
};

const resetCustomBenefitActivationOptions: ActivationDialogExtensions<CustomBenefitOptions>['onResetBenefitOptions'] = (
  selectedActivation,
  context,
): CustomBenefitOptions => {
  const monthlyGrant = selectedActivation?.customBenefitOptions?.monthlyGrant ?? context?.customBenefit?.defaultMonthlyGrant;
  const relevantForNonCash = selectedActivation?.customBenefitOptions?.relevantForNonCash ?? context?.customBenefit?.relevantForNonCash;
  return {
    monthlyGrant: monthlyGrant === undefined ? undefined : monthlyGrant / 100,
    supplier: selectedActivation?.customBenefitOptions?.supplier || '',
    comment: selectedActivation?.customBenefitOptions?.comment || '',
    relevantForNonCash: relevantForNonCash ?? false,
    customBenefitId: context?.customBenefit?.id,
  };
};

const createCustomBenefitActivation: ActivationDialogExtensions<CustomBenefitOptions>['onCreateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
  context,
) => {
  if (!context?.customBenefit?.id) {
    throw new Error('CustomBenefit must be set to create a benefitActivation for a custom benefit');
  }
  const create: CreateBenefitActivationDTO = {
    ...baseValues,
    benefit: BenefitDTOBenefitEnum.Custom,
    customBenefitOptions: {
      customBenefitId: context.customBenefit.id,
      monthlyGrant: benefitOptions.monthlyGrant === undefined ? undefined : Math.round(benefitOptions.monthlyGrant * 100),
      supplier: benefitOptions.supplier || undefined,
      comment: benefitOptions.comment || undefined,
      relevantForNonCash: benefitOptions.relevantForNonCash ?? false,
    },
  };
  await saveActivation(create);
};

const updateCustomBenefitActivation: ActivationDialogExtensions<CustomBenefitOptions>['onUpdateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
) => {
  const update: UpdateBenefitActivationDTO = {
    ...baseValues,
    customBenefitOptions: {
      monthlyGrant: benefitOptions.monthlyGrant === undefined ? null : Math.round(benefitOptions.monthlyGrant * 100),
      supplier: benefitOptions.supplier || null,
      comment: benefitOptions.comment || null,
      relevantForNonCash: benefitOptions.relevantForNonCash ?? false,
    },
  };
  await saveActivation(update);
};

export class CustomBenefitActivationDialogExtension extends BenefitActivationDialogExtensionInterface<CustomBenefitOptions> {
  CreateFields?: React.ReactNode = (<Fields />);
  ChangeOptionsFields?: React.ReactNode = (<Fields />);
  Header?: React.ComponentType = Header;
  InlineField?: React.ComponentType = InlineField;

  onCreateActivation = createCustomBenefitActivation;
  onUpdateActivation = updateCustomBenefitActivation;
  onResetBenefitOptions? = resetCustomBenefitActivationOptions;
}
